import { formatJstDateTime, instantFromIso } from "@nijiurachan/js/util/time";
import type { ReactNode } from "react";
import type {
  ConnectedApplicationsEvent,
  ConnectedApplicationsState,
} from "@/features/app-auth/connectedApplicationsMediator";
import { useConnectedApplicationsPresenter } from "@/features/app-auth/useConnectedApplicationsPresenter";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { Button } from "@/shared/ui/form";

export const ConnectedApplicationsPage: React.FunctionComponent = () => {
  const presentation = useConnectedApplicationsPresenter();
  return (
    <ComponentScope node={presentation.node}>
      <ConnectedApplicationsView
        {...presentation}
        onEvent={presentation.node.dispatch}
      />
    </ComponentScope>
  );
};

interface ConnectedApplicationsViewProps extends ConnectedApplicationsState {
  onEvent: (event: ConnectedApplicationsEvent) => void;
}

export function ConnectedApplicationsView({
  applications,
  phase,
  message,
  onEvent,
}: ConnectedApplicationsViewProps): ReactNode {
  const busy = phase !== "idle";
  return (
    <section className="flex-1 overflow-y-auto" aria-label="接続済みアプリ">
      <div className="mx-auto max-w-xl space-y-5 px-4 py-8">
        <h1 className="text-xl font-bold">接続済みアプリ</h1>
        <p>
          このブラウザで許可したアプリを確認し、端末ごとに利用許可を取り消せます。
        </p>
        <Button
          disabled={busy}
          onClick={() => onEvent({ type: "connected-applications.load" })}
        >
          接続済みアプリを確認
        </Button>
        <p role="status" aria-live="polite">
          {message}
        </p>
        <ul className="space-y-4">
          {applications?.map((application) => (
            <li
              key={application.id}
              className="rounded-lg border border-border p-4"
            >
              <strong>{application.clientName}</strong>
              <p>許可日時: {formatTimestamp(application.issuedAt)}</p>
              <p>有効期限: {formatTimestamp(application.expiresAt)}</p>
              {application.revokedAt !== null ? (
                <p>取り消し済み</p>
              ) : (
                <Button
                  variant="destructive"
                  disabled={busy}
                  onClick={() =>
                    onEvent({
                      type: "connected-applications.revoke",
                      id: application.id,
                    })
                  }
                >
                  この端末の許可を取り消す
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function formatTimestamp(value: string): string {
  const instant = instantFromIso(value);
  return instant === null
    ? "不明"
    : formatJstDateTime(instant, { dateStyle: "medium", timeStyle: "short" });
}
